import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { parseParametrizatioMinimumBillingData } from '@helper/grid'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import type {
  IParametrizationMinimumBillingContext,
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate
} from '@base/@types/providers/Parametrization/MinimumBillingProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationMinimumBillingHeader from '@base/variables/headers/parametrization/minimum-billing'

const ParametrizationMInimumBillingContext: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [loaddingEdit, setLoadingEdit] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [closeModal, setCloseModal] = useState(false)
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Faturamento Minimo - Relatório')
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const [data, updateData] = useImmer<any[]>([])

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [selecteds, _setSelecteds] = useState([] as any)
  const [selected, setSelectedOne] = useState(0)
  const [filterEdit, updateCenters] = useImmer({
    companies: [],
    suppliers: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    empresa: 0,
    fornecedor: 0,
    faturamentoMinimo: 0
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleUpdateEditParams = ({ name, value }: IUpdateParamsEdit) => {
    updateEditParams(draft => {
      draft[name] = value
    })
  }

  const getCenters = useCallback(async () => {
    setLoadingEdit(true)

    try {
      const { data: companies } = await parametrization.getCenters()
      const { data: suppliers } =
        await parametrization.getSuppliersForPurschase()

      updateCenters(draft => {
        return Object.assign(draft, {
          companies: parseCommonFilters(companies.data),
          suppliers: parseCommonFilters(suppliers.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationMinimumBillingHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationBillingHeader')) {
      encrypteHeaderStorange('parametrizationBillingHeader', headers)
    } else {
      localStorage.removeItem('parametrizationBillingHeader')
      encrypteHeaderStorange('parametrizationBillingHeader', headers)
    }
  }, [headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationBillingHeader')) {
      const newHeader = decrypterHeaderStorange('parametrizationBillingHeader')

      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
    })
  }
  const handlePageChange = useCallback(
    async (page: number) => {
      setLoading(true)
      parametrization
        .getMinimumBilling({
          page
        })
        .then(({ data: { data, pagination } }) => {
          updateData(data)
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
          updateData([])
        })
    },
    [updateData, updatePagination]
  )

  const exportData = useCallback(async () => {
    const configuracaoGrid = {
      id: headers[0].columns[0] ? headers[0].columns[0].show : true,
      empresaId: headers[1].columns[0] ? headers[1].columns[0].show : true,
      empresaNome: headers[1].columns[1] ? headers[1].columns[1].show : true,
      fornecedorId: headers[2].columns[0] ? headers[2].columns[0].show : true,
      fornecedorNome: headers[2].columns[1] ? headers[2].columns[1].show : true,
      faturamentoMinimo: headers[3].columns[0]
        ? headers[3].columns[0].show
        : true
    }
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSend = {
      configuracaoGrid
    }
    try {
      const { data: res } = await parametrization.postExportMinimumBilling(
        paramsToSend
      )
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      Swal.close()
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
  }, [toast, filename, headers])

  const getData = useCallback(async () => {
    setLoading(true)
    // await getMaintence()

    encrypteHeaderStorange('parametrizationBillingHeader', headers)

    handleStorange()

    try {
      parametrization
        .getMinimumBilling({
          page: 1
        })
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })

          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    } catch {
      setLoading(false)
    }

    setLoading(false)
  }, [headers, handleStorange, updateData, updatePagination])

  const createData = useCallback(async () => {
    try {
      const { data: _res } = await parametrization.postMinimumBilling({
        params: {
          empresaId: paramsEdit.empresa.value,
          fornecedorId: paramsEdit.fornecedor.value,
          faturamentoMinimo: Number(paramsEdit.faturamentoMinimo)
        }
      })
      toast({
        title: 'Sucesso.',
        description: 'Faturamento Minimo cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setCloseModal(true)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }

    setLoading(false)
    getData()
  }, [paramsEdit, getData, toast])

  const updateMinimumBilling = useCallback(async () => {
    try {
      const { data: _res } = await parametrization.putMinimumBilling({
        params: {
          id: Number(selected),
          empresaId: paramsEdit.empresa.value,
          fornecedorId: paramsEdit.fornecedor.value,
          faturamentoMinimo: Number(paramsEdit.faturamentoMinimo)
        }
      })
      toast({
        title: 'Sucesso.',
        description: 'Faturamento Minimo editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData()
  }, [paramsEdit, selected, setSelectedOne, getData, toast])

  const deleteData = useCallback(async () => {
    try {
      const { data: _res } = await parametrization.deleteMinimumBilling({
        idMinimumBilling: Number(selected)
      })
      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData()
  }, [selected, setLoading, setSelectedOne, getData, toast])

  const setData = useCallback(async () => {
    try {
      const { data: _res } = await parametrization.update({
        listaEditarEstoqueAbastecimentoCommand: []
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
  }, [toast])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return []
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterMinimumBilling',
            data,
            headers,
            parser: parseParametrizatioMinimumBillingData
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [data, headers, toggleColumn])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'empresa',
          value: {
            label: idData[0].empresa
              ? idData[0].idEmpresa + ' - ' + idData[0].empresa.descricao
              : '',
            value: idData[0].idEmpresa
          }
        })
        handleUpdateEditParams({
          name: 'fornecedor',
          value: {
            label: idData[0].fornecedor
              ? idData[0].idFornecedor + ' - ' + idData[0].fornecedor.descricao
              : '',
            value: idData[0].idFornecedor
          }
        })
        handleUpdateEditParams({
          name: 'faturamentoMinimo',
          value: Number(idData[0].faturamentoMinimo)
        })
      }
    },
    [data, handleUpdateEditParams]
  )
  const handleSelect = useCallback(
    async (index: any) => {
      const minimumBillingId = index['id-id']
      const newSelected = selected
      setSelectedOne(prev => (prev !== minimumBillingId ? minimumBillingId : 0))
      if (newSelected === minimumBillingId) {
        updateEditParams({
          empresa: 0,
          fornecedor: 0,
          faturamentoMinimo: 0
        })
      } else {
        handleEditParams(index['id-id'])
      }
    },
    [selected, updateEditParams, handleEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selected,
        isMulti: false
      }
    ]
  }, [handleSelect, selected])

  return (
    <ParametrizationMinimumBillingContext.Provider
      value={{
        loading,
        loaddingEdit,
        data: parseParametrizatioMinimumBillingData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selecteds,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        pagination,
        query,
        handlePageChange,
        filters,
        hiddenColumns,
        filtersList,
        closeModal,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        handleParamsDate,
        setQuery,
        getData,
        setData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,
        deleteData,
        exportData,
        getCenters,
        updateMinimumBilling,
        setFilters,
        setFilename
      }}
    >
      {children}
    </ParametrizationMinimumBillingContext.Provider>
  )
}

export default ParametrizationMInimumBillingContext
export const ParametrizationMinimumBillingContext =
  createContext<IParametrizationMinimumBillingContext>(
    {} as IParametrizationMinimumBillingContext
  )
